import { assert, assertIsArray, assertIsNotUndefined } from "../utils/assertOfType";
import { snakeCaseToPascalCase } from "../utils/strings";
import type {
  ArraySchema,
  AnySchema,
  NullishSchema,
  BooleanSchema,
  NumberSchema,
  StringSchema,
  Schema,
  EnumSchema,
  LiteralSchema,
  IntersectionSchema,
  MappedObjectSchema,
  ObjectSchema,
  UnionSchema,
  PartialSchema,
  TypedArraySchema,
  InterfaceSchema,
  ManualSchema,
  OmitSchema,
  TypeIdentifierSchema,
  TupleSchema,
  PickSchema,
  ObjectPropertySchema,
  TypeParameterSchema,
  TypeReferenceSchema,
  ForeignKeySchema,
  ForeignKeyStringSchema,
  ForeignKeyTableSchema,
  ForeignKeyHashTableSchema,
  ForeignKeyHashJoinTableSchema,
  NominalNumberSchema,
  DeletedSchema,
  MovedSchema,
  PrimaryKeySchema,
  PrimaryKeyTableSchema,
} from "./schema";
import { union, undef, interf, partial, objectProp } from "./schema";
import type { Table } from "./table";

export interface ValidationError {
  readonly error: string;
  readonly stack: Schema[];
}

export interface ValidationErrorNoStack {
  readonly error: string;
}

export interface ValidationErrorResult {
  readonly type: "FAIL";
  readonly errors: ValidationError[];
}

export interface ValidationWarning {
  readonly warning: string;
  readonly stack: Schema[];
}

export interface ValidationSuccessResult {
  readonly type: "SUCCESS";
  readonly warnings: ValidationWarning[];
}

export type ValidationResult = ValidationErrorResult | ValidationSuccessResult;

export function schemaHasName(schema: Schema): schema is Schema & { name: string } {
  return typeof (schema as any).name === "string";
}

export function isAtomSchema(schema: Schema): boolean {
  return (
    schema.kind === "ANY" ||
    schema.kind === "NULLISH" ||
    schema.kind === "BOOLEAN" ||
    schema.kind === "NUMBER" ||
    schema.kind === "STRING"
  );
}

export function formatSchema(schema: Schema): string {
  if (schema.kind === "OBJECT") {
    return schema.name
      ? `${schema.name}<${formatSchema(schema.valueType)}>`
      : "{ [key: string]: ${formatSchema(schema.valueType) }";
  }

  if (schemaHasName(schema)) {
    return schema.name;
  }

  if (schema.kind === "NULLISH") {
    return schema.subType;
  } else if (isAtomSchema(schema)) {
    return schema.kind.toLowerCase();
  }

  if (schema.kind === "PRIMARY_KEY") {
    return schema.kind;
  }

  if (schema.kind === "UNION") {
    return schema.unionTypes.map(formatSchema).join(" | ");
  }

  if (schema.kind === "LITERAL") {
    return String(schema.value);
  }

  if (schema.kind === "INTERSECTION") {
    return schema.intersectionTypes.map(formatSchema).join(" & ");
  }

  return `Anonymous${snakeCaseToPascalCase(schema.kind)}`;
}

export function formatValidationError(failure: ValidationError): string {
  return `Error at ${failure.stack.map(formatSchema).join(".")}:\n${indent(failure.error)}`;
}

export function formatValidationErrorResult(failure: ValidationErrorResult, maxErrors?: number): string {
  const res = failure.errors
    .slice(0, maxErrors)
    .map(formatValidationError)
    .map((x, i) => `${i + 1}. ${x}`)
    .join("\n");

  return `Validation failed with ${failure.errors.length} errors${
    typeof maxErrors === "number" ? ` (Max ${maxErrors})` : ""
  }:\n${indent(res)}`;
}

export function formatValidationWarning(warning: ValidationWarning): string {
  return `Warning at ${warning.stack.map(formatSchema).join(".")}:\n${indent(warning.warning)}`;
}

export class SchemaValidator {
  private typeResolver: SchemaResolver;

  constructor(
    private readonly library: Table<Schema>,
    private readonly validateManualType: (
      value: any,
      schema: ManualSchema,
    ) => ValidationErrorNoStack[] | undefined = () => [],
    private validateForeignKeyInternal: (
      value: string,
      type?: string,
      fieldPath?: string,
      currentObject?: any,
    ) => ValidationErrorNoStack[] | undefined = () => [],
    private validatePrimaryKeyInternal: (
      value: string,
      skipRekey: boolean,
      type?: string,
      fieldPath?: string,
      currentObject?: any,
    ) => ValidationErrorNoStack[] | undefined = () => [],
    private readonly allowStrictInterfaceFieldChecks = true,
  ) {
    this.typeResolver = new SchemaResolver(library);
  }

  private errors: ValidationError[] = [];
  private warnings: ValidationWarning[] = [];
  private schemaStack: Schema[] = [];
  private fieldPathStack: string[] = [];
  private currentObjectStack: any[] = [];

  private validatePrimaryKeyTableInternal: (
    value: Record<string, unknown>,
    skipRekey: boolean,
    type?: string,
    fieldPath?: string,
    currentObject?: any,
  ) => ValidationErrorNoStack[] | undefined = () => [];

  private validateForeignKeyTableInternal: (
    value: Record<string, unknown>,
    type?: string,
    fieldPath?: string,
    currentObject?: any,
  ) => ValidationErrorNoStack[] | undefined = () => [];

  private validateForeignKeyHashTableInternal: (
    value: Record<string, unknown>,
    type?: string,
    fieldPath?: string,
    currentObject?: any,
  ) => ValidationErrorNoStack[] | undefined = () => [];

  validate(input: any, schema: Schema): ValidationResult {
    this.errors = [];
    this.warnings = [];
    this.schemaStack = [];
    this.fieldPathStack = [];
    this.currentObjectStack = [input]; // Initialize with root object

    this.validateInternal(input, schema);

    if (this.errors.length > 0) {
      return {
        type: "FAIL",
        errors: this.errors,
      };
    }

    return {
      type: "SUCCESS",
      warnings: this.warnings,
    };
  }

  setValidatePrimaryKey(
    validatePrimaryKey: (
      value: string,
      skipRekey: boolean,
      type?: string,
      fieldPath?: string,
      currentObject?: any,
    ) => ValidationErrorNoStack[] | undefined,
  ) {
    this.validatePrimaryKeyInternal = validatePrimaryKey;
  }

  setValidatePrimaryKeyTable(
    validatePrimaryKeyTable: (
      value: Record<string, unknown>,
      skipRekey: boolean,
      type?: string,
      fieldPath?: string,
      currentObject?: any,
    ) => ValidationErrorNoStack[] | undefined,
  ) {
    this.validatePrimaryKeyTableInternal = validatePrimaryKeyTable;
  }

  setValidateForeignKeyTable(
    validateForeignKeyTable: (
      value: Record<string, unknown>,
      type?: string,
      fieldPath?: string,
      currentObject?: any,
    ) => ValidationErrorNoStack[] | undefined,
  ) {
    this.validateForeignKeyTableInternal = validateForeignKeyTable;
  }

  setValidateForeignKeyHashTable(
    validateForeignKeyHashTable: (
      value: Record<string, unknown>,
      type?: string,
      fieldPath?: string,
      currentObject?: any,
    ) => ValidationErrorNoStack[] | undefined,
  ) {
    this.validateForeignKeyHashTableInternal = validateForeignKeyHashTable;
  }

  private validateInternal(input: any, schema: Schema): boolean {
    this.schemaStack.push(schema);

    let res = true;

    switch (schema.kind) {
      case "PRIMARY_KEY":
        res = this.validatePrimaryKey(input, schema);
        break;
      case "PRIMARY_KEY_TABLE":
        res = this.validatePrimaryKeyTable(input, schema);
        break;
      case "FOREIGN_KEY":
        res = this.validateForeignKey(input, schema);
        break;
      case "FOREIGN_KEY_PATH":
        res = this.validateForeignKeyPath(input, schema as any);
        break;
      case "FOREIGN_KEY_TABLE":
        res = this.validateForeignKeyTable(input, schema);
        break;
      case "FOREIGN_KEY_HASH_TABLE":
        res = this.validateForeignKeyHashTable(input, schema as ForeignKeyHashTableSchema);
        break;
      case "FOREIGN_KEY_HASH_JOIN_TABLE":
        res = this.validateForeignKeyHashJoinTable(input, schema as ForeignKeyHashJoinTableSchema);
        break;
      case "FOREIGN_KEY_STRING":
        res = this.validateForeignKeyString(input, schema);
        break;
      case "OBJECT_PROPERTY":
        res = this.validateObjectProperty(input, schema);
        break;
      case "ANY":
        res = this.validateAny(input, schema);
        break;
      case "NULLISH":
        res = this.validateNullish(input, schema);
        break;
      case "BOOLEAN":
        res = this.validateBoolean(input, schema);
        break;
      case "NUMBER":
        res = this.validateNumber(input, schema);
        break;
      case "STRING":
        res = this.validateString(input, schema);
        break;
      case "TYPE_REFERENCE":
        res = this.validateTypeReference(input, schema);
        break;
      case "TYPE_PARAMETER":
        res = this.validateTypeParameter(input, schema);
        break;
      case "ARRAY":
        res = this.validateArray(input, schema);
        break;
      case "INTERFACE":
        res = this.validateInterface(input, schema);
        break;
      case "MANUAL":
        res = this.validateManual(input, schema);
        break;
      case "MAPPED_OBJECT":
        res = this.validateMappedObject(input, schema);
        break;
      case "OMIT":
        res = this.validateOmit(input, schema);
        break;
      case "PICK":
        res = this.validatePick(input, schema);
        break;
      case "IDENTIFIER":
        res = this.validateIdentifier(input, schema);
        break;
      case "TUPLE":
        res = this.validateTuple(input, schema);
        break;
      case "OBJECT":
        res = this.validateObject(input, schema);
        break;
      case "UNION":
        res = this.validateUnion(input, schema);
        break;
      case "ENUM":
        res = this.validateEnum(input, schema);
        break;
      case "LITERAL":
        res = this.validateLiteral(input, schema);
        break;
      case "PARTIAL":
        res = this.validatePartial(input, schema);
        break;
      case "INTERSECTION":
        res = this.validateIntersection(input, schema);
        break;
      case "TYPED_ARRAY":
        res = this.validateTypedArray(input, schema);
        break;
      case "NOMINAL_NUMBER":
        res = this.validateNominalNumber(input, schema);
        break;
      case "DELETED":
        res = this.validateDeleted(input, schema);
        break;
      case "MOVED":
        res = this.validateMoved(input, schema);
        break;
      case "DEPRECATED":
        this.warnings.push({
          warning: `Schema ${schema.schema.kind} is deprecated here`,
          stack: this.schemaStack.slice(),
        });
        res = this.validateInternal(input, schema.schema);
        break;
      case "DESCRIBED":
        res = this.validateInternal(input, schema.schema);
        break;
    }

    this.schemaStack.pop();

    return res;
  }

  private lookupTypeArgument(name: string, start = 0): Schema {
    for (let i = start, l = this.schemaStack.length; i < l; i++) {
      const schema = this.schemaStack[l - 1 - i];

      if (schema.kind === "TYPE_REFERENCE") {
        if (schema.typeArgs && name in schema.typeArgs) {
          const resolved = schema.typeArgs[name];

          assertIsNotUndefined(resolved, "could not resolve type parameter to argument");

          if (resolved.kind === "TYPE_PARAMETER") {
            return this.lookupTypeArgument(resolved.name, i + 1);
          }

          return resolved;
        }
      }
    }

    throw new Error("Could not find type parameter in scope!");
  }

  private validateTypeParameter(input: any, schema: TypeParameterSchema): boolean {
    const argSchema = this.lookupTypeArgument(schema.name);

    assert(argSchema.kind !== "TYPE_PARAMETER", "Resolved type parameter to another type parameter");

    return this.validateInternal(input, argSchema);
  }

  private validateTypeReference(input: any, schema: TypeReferenceSchema): boolean {
    // This looks like nothing happens, but type parameters in target type
    // will be handled by referring to the typeArgs field of the TypeReferenceSchema
    // in the schemaStack via lookupTypeArgument
    return this.validateInternal(input, schema.targetType);
  }

  private validateObjectProperty(input: any, schema: ObjectPropertySchema): boolean {
    this.fieldPathStack.push(schema.name);

    // The current object is the one that contains this property
    // We don't change the current object stack here because we want to know
    // what object contains the field we're validating, not the field value itself
    const result = this.validateInternal(input, schema.schema);

    this.fieldPathStack.pop();

    return result;
  }

  private getCurrentFieldPath(): string {
    return this.fieldPathStack.join(".");
  }

  private getCurrentObject(): any {
    return this.currentObjectStack[this.currentObjectStack.length - 1];
  }

  private validateForeignKey(input: any, schema: ForeignKeySchema): boolean {
    if (typeof input !== "string") {
      return this.check(false, `Expected string, got ${typeof input}`);
    }

    const schemaType = (() => {
      if (Array.isArray(schema.type)) {
        return schema.type.length === 1 ? schema.type[0] : schema.type;
      }
      return schema.type;
    })();

    const errors = this.validateForeignKeyInternal(
      input,
      /**
       * We cast this as a string here so as not to convolute the SchemaValidator internals. The necessary pieces
       * of the foreign key validation logic & key collection are wired up to handle this being string | string[] internally.
       */
      schemaType as string,
      this.getCurrentFieldPath(),
      this.getCurrentObject(),
    );

    if (errors?.length) {
      this.errors.push(...errors.map((x) => ({ ...x, stack: this.schemaStack.slice() })));
      return false;
    }

    return true;
  }

  private validateForeignKeyPath(input: any, schema: { readonly type: string[] }): boolean {
    return this.validateForeignKey(input, { kind: "FOREIGN_KEY", type: schema.type });
  }

  private validatePrimaryKey(input: any, schema: PrimaryKeySchema): boolean {
    if (typeof input !== "string") {
      return this.check(false, `Expected string, got ${typeof input}`);
    }

    const errors = this.validatePrimaryKeyInternal(
      input,
      schema.skipRekey,
      schema.type,
      this.getCurrentFieldPath(),
      this.getCurrentObject(),
    );

    if (errors?.length) {
      this.errors.push(...errors.map((x) => ({ ...x, stack: this.schemaStack.slice() })));
      return false;
    }

    return true;
  }

  private validatePrimaryKeyTable(input: any, schema: PrimaryKeyTableSchema): boolean {
    if (!this.check(typeof input === "object", "Expected object")) {
      return false;
    }

    if (!this.check(input !== null, "Expected object, got null")) {
      return false;
    }

    if (!this.check(!Array.isArray(input), "Expected object, got array")) {
      return false;
    }

    const errors = this.validatePrimaryKeyTableInternal(
      input,
      schema.skipRekey,
      schema.type,
      this.getCurrentFieldPath(),
      this.getCurrentObject(),
    );
    if (errors?.length) {
      this.errors.push(...errors.map((x) => ({ ...x, stack: this.schemaStack.slice() })));
    }

    return Object.keys(input)
      .map((key) => this.validateInternal(input[key], objectProp(key, schema.valueType)))
      .every((x) => !!x);
  }

  private validateForeignKeyTable(input: any, schema: ForeignKeyTableSchema): boolean {
    if (!this.check(typeof input === "object", "Expected object")) {
      return false;
    }

    if (!this.check(input !== null, "Expected object, got null")) {
      return false;
    }

    if (!this.check(!Array.isArray(input), "Expected object, got array")) {
      return false;
    }

    // Handle both single type and union types
    const types = Array.isArray(schema.type) ? schema.type : [schema.type];

    // For union types, we need to validate each key against all possible types
    // The foreign key collector will handle determining which type each key actually references
    for (const type of types) {
      const errors = this.validateForeignKeyTableInternal(
        input,
        type,
        this.getCurrentFieldPath(),
        this.getCurrentObject(),
      );
      if (errors?.length) {
        this.errors.push(...errors.map((x) => ({ ...x, stack: this.schemaStack.slice() })));
      }
    }

    return Object.keys(input)
      .map((key) => this.validateInternal(input[key], objectProp(key, schema.valueType)))
      .every((x) => !!x);
  }

  private validateForeignKeyHashTable(input: any, schema: ForeignKeyHashTableSchema): boolean {
    return this.validateForeignKeyHashTableCommon(input, schema);
  }

  private validateForeignKeyHashJoinTable(input: any, schema: ForeignKeyHashJoinTableSchema): boolean {
    return this.validateForeignKeyHashTableCommon(input, schema);
  }

  private validateForeignKeyHashTableCommon(
    input: any,
    schema: ForeignKeyHashTableSchema | ForeignKeyHashJoinTableSchema,
  ): boolean {
    if (!this.check(typeof input === "object", "Expected object")) {
      return false;
    }

    if (!this.check(input !== null, "Expected object, got null")) {
      return false;
    }

    if (!this.check(!Array.isArray(input), "Expected object, got array")) {
      return false;
    }

    const types = Array.isArray(schema.type) ? schema.type : [schema.type];

    for (const type of types) {
      const errors = this.validateForeignKeyHashTableInternal(
        input,
        type,
        this.getCurrentFieldPath(),
        this.getCurrentObject(),
      );
      if (errors?.length) {
        this.errors.push(...errors.map((x) => ({ ...x, stack: this.schemaStack.slice() })));
      }
    }

    return Object.keys(input)
      .map((key) => this.validateInternal(input[key], objectProp(key, schema.valueType)))
      .every((x) => !!x);
  }

  private validateForeignKeyString(input: any, _schema: ForeignKeyStringSchema): boolean {
    if (!this.check(typeof input === "string", `Expected string, got ${typeof input}`)) {
      return false;
    }

    // ForeignKeyString validation is handled by the foreign key extraction system
    // which can detect both parameter tokens and formula references
    const errors = this.validateForeignKeyInternal(
      input,
      undefined, // No specific type since ForeignKeyString handles multiple reference types
      this.getCurrentFieldPath(),
      this.getCurrentObject(),
    );

    if (errors?.length) {
      this.errors.push(...errors.map((x) => ({ ...x, stack: this.schemaStack.slice() })));
      return false;
    }

    return true;
  }

  private check(condition: boolean, error: string) {
    if (!condition) {
      this.errors.push({
        error,
        stack: this.schemaStack.slice(),
      });
    }

    return condition;
  }

  private validateAny(input: any, schema: AnySchema): boolean {
    return this.validateAtom(input, schema);
  }

  private validateNullish(input: any, schema: NullishSchema): boolean {
    return this.validateAtom(input, schema);
  }

  private validateBoolean(input: any, schema: BooleanSchema): boolean {
    return this.validateAtom(input, schema);
  }

  private validateNumber(input: any, schema: NumberSchema): boolean {
    return this.validateAtom(input, schema);
  }

  private validateString(input: any, schema: StringSchema): boolean {
    return this.validateAtom(input, schema);
  }

  private validateAtom(input: any, schema: Schema): boolean {
    const res = this.validateAtomInternal(input, schema);
    const expected = schema.kind === "NULLISH" ? schema.subType : schema.kind;

    // We construct the template literal in a conditional for performance reasons
    // as this is a hot path
    if (!res) {
      return this.check(false, `Expected ${expected.toLowerCase()}, got ${input === null ? "null" : typeof input}`);
    }
    return res;
  }

  private validateAtomInternal(input: any, schema: Schema): boolean {
    switch (schema.kind) {
      case "STRING":
        return typeof input === "string";
      case "NUMBER":
        return typeof input === "number";
      case "BOOLEAN":
        return typeof input === "boolean";
      case "NULLISH":
        return schema.subType === "undefined" ? input === undefined : input === null;
      case "ANY":
      default:
        return true;
    }
  }

  private validateArray(input: any, schema: ArraySchema): boolean {
    if (!this.check(Array.isArray(input), "Expected array")) {
      return false;
    }

    // Push the current array to the stack since we're entering a new array scope
    this.currentObjectStack.push(input);

    const result = input
      .map((item: any, id: number) => this.validateInternal(item, objectProp(id, schema.arrayType)))
      .every((x: boolean) => !!x);

    // Pop the array before returning
    this.currentObjectStack.pop();

    return result;
  }

  private validateInterface(input: any, schema: InterfaceSchema, ensureStrictFieldCount = true): boolean {
    if (!this.check(typeof input === "object", `Expected object, got ${typeof input}`)) {
      return false;
    }

    if (!this.check(input !== null, "Expected object, got null")) {
      return false;
    }

    if (!this.check(!Array.isArray(input), "Expected object, got array")) {
      return false;
    }

    // Push the current object to the stack since we're entering a new object scope
    this.currentObjectStack.push(input);

    const schemaFields = Object.keys(schema.fields);

    let res = schemaFields
      .map((x) => this.validateInternal(input[x], objectProp(x, schema.fields[x])))
      .every((x) => !!x);

    if (!ensureStrictFieldCount || !this.allowStrictInterfaceFieldChecks) {
      // Pop the object before returning
      this.currentObjectStack.pop();
      return res;
    }

    if (res) {
      for (const field of Object.keys(input)) {
        res = res && this.check(schema.fields[field] !== undefined, `Unexpected field: ${field}`);
      }
    }

    // Pop the object before returning
    this.currentObjectStack.pop();
    return res;
  }

  private validateObject(input: any, schema: ObjectSchema): boolean {
    if (!this.check(typeof input === "object", "Expected object")) {
      return false;
    }

    if (!this.check(input !== null, "Expected object, got null")) {
      return false;
    }

    if (!this.check(!Array.isArray(input), "Expected object, got array")) {
      return false;
    }

    return Object.keys(input)
      .map((key) => this.validateInternal(input[key], objectProp(key, schema.valueType)))
      .every((x) => !!x);
  }

  private validateManual(input: any, schema: ManualSchema): boolean {
    const failures = this.validateManualType(input, schema);

    if (failures) {
      this.errors.push(...failures.map((x) => ({ ...x, stack: this.schemaStack.slice() })));
      return false;
    }

    return true;
  }

  private validateMappedObject(input: any, schema: MappedObjectSchema): boolean {
    if (!this.check(typeof input === "object", "Expected object")) {
      return false;
    }

    if (!this.check(input !== null, "Expected object, got null")) {
      return false;
    }

    const keys = Object.keys(input);

    return keys
      .map(
        (key) =>
          this.validateInternal(key, schema.indexType) &&
          this.validateInternal(input[key], objectProp(key, schema.valueType)),
      )
      .every((x) => !!x);
  }

  private validateOmit(input: any, schema: OmitSchema): boolean {
    if (!this.check(typeof input === "object", "Expected object")) {
      return false;
    }

    for (const key in input) {
      if (!this.validateInternal(input[key], objectProp(key, schema.valueType))) {
        return false;
      }
    }

    return true;
  }

  private validateIdentifier(input: any, schema: TypeIdentifierSchema): boolean {
    const refType = this.library[schema.name];

    if (!this.check(!!refType, `Reference type not found in library: ${schema.name}`)) {
      return false;
    }

    return this.validateInternal(input, refType);
  }

  private validateTuple(input: any, schema: TupleSchema): boolean {
    if (!this.check(Array.isArray(input), "Expected array")) {
      return false;
    }

    if (!this.check(input.length === schema.tupleTypes.length, "Expected array")) {
      return false;
    }

    assertIsArray(input);

    return input.every((item, i) => this.validateInternal(item, objectProp(i, schema.tupleTypes[i])));
  }

  private cloneEmpty() {
    const clone = new SchemaValidator(
      this.library,
      this.validateManualType,
      this.validateForeignKeyInternal,
      this.validatePrimaryKeyInternal,
      this.allowStrictInterfaceFieldChecks,
    );

    // Only set callbacks that are NOT passed to constructor
    clone.setValidatePrimaryKeyTable(this.validatePrimaryKeyTableInternal);
    clone.setValidateForeignKeyTable(this.validateForeignKeyTableInternal);
    clone.setValidateForeignKeyHashTable(this.validateForeignKeyHashTableInternal);

    return clone;
  }

  private cloneForErrorCollection() {
    // Provide dummy validators that avoid side effects but still yield a FAIL result for probing
    const fkSuppressed = (_v: string, _t?: string | string[], _f?: string, _o?: any) => [
      { error: "FK_VALIDATION_SUPPRESSED" },
    ];
    const pkSuppressed = (_v: string, _skipRekey: boolean, _t?: string, _f?: string, _o?: any) => [
      { error: "PK_VALIDATION_SUPPRESSED" },
    ];
    const clone = new SchemaValidator(
      this.library,
      this.validateManualType,
      fkSuppressed,
      pkSuppressed,
      this.allowStrictInterfaceFieldChecks,
    );
    // Also cause table validations to fail without side effects
    clone.setValidatePrimaryKeyTable((_v) => [{ error: "PK_TABLE_VALIDATION_SUPPRESSED" }]);
    clone.setValidateForeignKeyTable((_v) => [{ error: "FK_TABLE_VALIDATION_SUPPRESSED" }]);
    clone.setValidateForeignKeyHashTable((_v) => [{ error: "FK_HASH_TABLE_VALIDATION_SUPPRESSED" }]);
    return clone;
  }

  private setFieldPathStack(fieldPathStack: string[]) {
    this.fieldPathStack = [...fieldPathStack];
  }

  private setCurrentObjectStack(currentObjectStack: any[]) {
    this.currentObjectStack = [...currentObjectStack];
  }

  private cloneWithFieldPath() {
    const clone = this.cloneEmpty();

    // Preserve field path context by copying the field path stack
    clone.setFieldPathStack(this.fieldPathStack);

    // Preserve object context by copying the current object stack
    clone.setCurrentObjectStack(this.currentObjectStack);

    return clone;
  }

  private validateUnion(input: any, schema: UnionSchema): boolean {
    const results: ValidationErrorResult[] = [];

    const typeDiscriminator = this.typeDiscriminatorFieldFromUnionType(schema);

    if (typeof input === "object" && typeDiscriminator) {
      const keyOfInput = input[typeDiscriminator];

      if (!keyOfInput) {
        return this.check(false, `Expected type discriminator field ${typeDiscriminator}`);
      }

      const discriminatorSchema = schema.unionTypes
        .map((x) => this.typeResolver.resolveSchema(x))
        .find((x) => {
          if (x.kind !== "INTERFACE") {
            return false;
          }

          const field = x.fields[typeDiscriminator];

          if (field?.kind === "LITERAL" && field.value === keyOfInput) {
            return true;
          }

          return false;
        });

      if (!discriminatorSchema) {
        return this.check(
          false,
          `No schema found for type with discriminator "${typeDiscriminator}"=${JSON.stringify(
            keyOfInput,
          )} in union type`,
        );
      }

      // For discriminated unions, use validateInternal to preserve field paths
      const validator = this.cloneWithFieldPath();
      const isValid = validator.validateInternal(input, discriminatorSchema);

      if (isValid) {
        return true;
      }

      // For error reporting, use separate validator with FK/PK disabled to avoid side effects
      const errorValidator = this.cloneForErrorCollection();
      const res = errorValidator.validate(input, discriminatorSchema);

      return this.check(
        false,
        `Expected ${formatSchema(
          discriminatorSchema,
        )} due to union type discriminator field ("${typeDiscriminator}": "${keyOfInput}")${
          res.type === "FAIL" ? `\n${indent(formatValidationErrorResult(res))}` : ""
        }`,
      );
    }

    // Try each union type using validateInternal to preserve field paths
    for (const unionSchema of schema.unionTypes) {
      const validator = this.cloneWithFieldPath();
      const isValid = validator.validateInternal(input, unionSchema);
      if (isValid) {
        return true;
      }

      // For error collection, use separate validator ONLY on validation failure with FK/PK disabled
      const errorValidator = this.cloneForErrorCollection();
      const res = errorValidator.validate(input, unionSchema);
      if (res.type === "FAIL") {
        results.push(res);
      }
    }

    let minErrorCount: { index: number; schema: Schema | undefined; count: number } = {
      index: 0,
      schema: undefined,
      count: Number.POSITIVE_INFINITY,
    };

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const s = schema.unionTypes[i];

      if (result.errors.length < minErrorCount.count && !isAtomSchema(s)) {
        minErrorCount = {
          index: i,
          schema: s,
          count: result.errors.length,
        };
      }
    }

    if (minErrorCount.schema === undefined) {
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        const s = schema.unionTypes[i];

        if (result.errors.length < minErrorCount.count) {
          minErrorCount = {
            index: i,
            schema: s,
            count: result.errors.length,
          };
        }
      }
    }

    assertIsNotUndefined(minErrorCount.schema);

    return this.check(
      false,
      `No type matches union type \n${indent(
        results.map((x, i) => `${formatSchema(schema.unionTypes[i])}: ${x.errors.length} errors`).join("\n"),
      )}\nMatching ${formatSchema(minErrorCount.schema)}:\n${indent(
        results[minErrorCount.index].errors.map(formatValidationError).join("\n"),
      )}`,
    );
  }

  private typeDiscriminatorFieldFromUnionType(unionSchema: UnionSchema): string | undefined {
    const types = unionSchema.unionTypes.map((x) => this.typeResolver.resolveSchema(x));

    // limitation here is we can't resolve non-interface types
    if (!types.every((x) => x.kind === "INTERFACE")) {
      return undefined;
    }

    // find a field on every interface that has the same name, is a literal, but isn't
    // the same value on each interface
    const discriminatorFields: string[] = Object.keys(types[0].fields).filter(
      (x) => types[0].fields[x].kind === "LITERAL",
    );

    let discriminator: string | undefined;
    for (const field of discriminatorFields) {
      // field exists on each type and is a literal
      const fieldOnEachType = types.every((x) => x.fields[field]?.kind === "LITERAL");
      if (!fieldOnEachType) {
        continue;
      }

      // the value of the field is different on each type
      const values = types.map((x) => x.fields[field].kind === "LITERAL" && x.fields[field].value);

      if (values.length === new Set(values).size) {
        discriminator = field;
        break;
      }
    }

    return discriminator;
  }

  private validateEnum(input: any, schema: EnumSchema): boolean {
    const res = Object.values(schema.members).includes(input);

    if (!res) {
      return this.check(res, `Expected one of the enum values, got ${input}`);
    }

    return res;
  }

  private validateLiteral(input: any, schema: LiteralSchema): boolean {
    return this.check(input === schema.value, `Expected to be ${schema.value}`);
  }

  private validatePartial(input: any, schema: PartialSchema): boolean {
    if (!this.check(typeof input === "object", "Expected object")) {
      return false;
    }

    assert(typeof input === "object");

    const resolvedType = this.typeResolver.resolveSchema(schema);

    if (resolvedType.kind === "INTERFACE") {
      return this.validateInterface(input, resolvedType, false);
    }

    return this.validateInternal(input, resolvedType);
  }

  private validatePick(input: any, schema: PickSchema): boolean {
    if (!this.check(typeof input === "object", "Expected object")) {
      return false;
    }

    const resolvedType = this.typeResolver.resolveSchema(schema);

    if (resolvedType.kind === "INTERFACE") {
      return this.validateInterface(input, resolvedType, false);
    }

    return this.validateInternal(input, resolvedType);
  }

  private validateIntersection(input: any, schema: IntersectionSchema): boolean {
    const interfType = this.typeResolver.resolveSchema(schema);
    return this.validateInternal(input, interfType);
  }

  private validateTypedArray(input: any, schema: TypedArraySchema): boolean {
    switch (schema.name) {
      case "Float64Array": {
        if (!this.check(input instanceof Float64Array, "Expected Float64Array")) {
          return false;
        }
        return true;
      }
      case "Int8Array": {
        if (!this.check(input instanceof Int8Array, "Expected Int8Array")) {
          return false;
        }
        return true;
      }
      case "Int16Array": {
        if (!this.check(input instanceof Int16Array, "Expected Int16Array")) {
          return false;
        }
        return true;
      }
      case "Int32Array": {
        if (!this.check(input instanceof Int32Array, "Expected Int32Array")) {
          return false;
        }
        return true;
      }
      case "Uint8Array": {
        if (!this.check(input instanceof Uint8Array, "Expected Uint8Array")) {
          return false;
        }
        return true;
      }
      case "Uint8ClampedArray": {
        if (!this.check(input instanceof Uint8ClampedArray, "Expected Uint8ClampedArray")) {
          return false;
        }
        return true;
      }
      case "Float32Array": {
        if (!this.check(input instanceof Float32Array, "Expected Float32Array")) {
          return false;
        }
        return true;
      }
      case "Uint16Array": {
        if (!this.check(input instanceof Uint16Array, "Expected Uint16Array")) {
          return false;
        }
        return true;
      }
      case "Uint32Array": {
        if (!this.check(input instanceof Uint32Array, "Expected Uint32Array")) {
          return false;
        }
        return true;
      }
    }
  }

  private validateNominalNumber(input: any, _: NominalNumberSchema): boolean {
    return this.check(typeof input === "number", "Expected number");
  }

  private validateDeleted(input: any, _: DeletedSchema): boolean {
    return this.check(input === null || input === undefined, "Expected null or undefined");
  }

  private validateMoved(input: any, _: MovedSchema): boolean {
    return this.check(input === null || input === undefined, "Expected null or undefined");
  }
}

export class SchemaResolver {
  constructor(private readonly library: Table<Schema>) {}

  private cache = new Map<Schema, Schema>();

  resolveSchema(schema: Schema): Schema {
    const cachedSchema = this.cache.get(schema);
    if (cachedSchema) {
      return cachedSchema;
    }

    const result = this.resolveSchemaInternal(schema);
    this.cache.set(schema, result);
    return result;
  }

  resolveSchemaInternal(schema: Schema): Schema {
    switch (schema.kind) {
      case "TYPE_REFERENCE": {
        const { targetType, typeArgs } = schema;

        if (targetType.kind === "INTERFACE") {
          // TODO extend to other types
          const { fields: unevaluatedFields } = targetType;

          return interf(
            Object.keys(unevaluatedFields).reduce(
              (acc, key) => {
                if (unevaluatedFields[key].kind === "TYPE_PARAMETER") {
                  const typeArg = typeArgs[unevaluatedFields[key].name];
                  if (!typeArg) {
                    throw new Error(`Type argument not found: ${unevaluatedFields[key].name}`);
                  }

                  acc[key] = typeArg;
                } else {
                  acc[key] = unevaluatedFields[key];
                }

                return acc;
              },
              {} as Record<string, Schema>,
            ),
          );
        } else {
          return targetType;
        }
      }
      case "MAPPED_OBJECT": {
        const indexType = this.resolveSchema(schema.indexType);

        if (indexType.kind === "LITERAL") {
          // if the index is a literal, we can just use the value
          return interf({
            [indexType.value.toString()]: this.resolveSchema(schema.valueType),
          });
        }

        if (indexType.kind === "ENUM") {
          const valueType = this.resolveSchema(schema.valueType);

          const fields: Record<string, Schema> = {};
          for (const field in indexType.members) {
            fields[field] = valueType;
          }

          // if the index is a literal, we can just use the value
          return interf(fields);
        }

        return schema;
      }
      case "INTERSECTION": {
        const resolvedTypes = schema.intersectionTypes.map((key) => this.resolveSchema(key));

        const manualSchema = resolvedTypes.find((x) => x.kind === "MANUAL");
        if (manualSchema) {
          return manualSchema;
        }

        const interfaceTypes = resolvedTypes.filter((t) => t.kind === "INTERFACE");
        if (interfaceTypes.length > 0) {
          return interf(
            (interfaceTypes as InterfaceSchema[]).reduce(
              (acc, key) => {
                Object.assign(acc, key.fields);
                return acc;
              },
              {} as Record<string, Schema>,
            ),
          );
        }

        // If no interfaces, return first resolved type to avoid throwing
        return resolvedTypes[0] ?? schema;
      }
      case "IDENTIFIER": {
        const idType = this.library[schema.name];
        assertIsNotUndefined(idType, `Type not found in library: ${schema.name}`);

        return this.resolveSchema(idType);
      }
      case "INTERFACE": {
        return schema;
      }
      case "PARTIAL": {
        // convert each field to an undefined union
        const valueType = this.resolveSchema(schema.valueType);

        if (valueType.kind === "INTERFACE") {
          return interf(
            Object.keys(valueType.fields).reduce(
              (acc, key) => {
                const fieldSchema = valueType.fields[key];

                // if its already a union of undefined, skip
                if (
                  fieldSchema.kind === "UNION" &&
                  fieldSchema.unionTypes.some((x) => x.kind === "NULLISH" && x.subType === "undefined")
                ) {
                  return acc;
                }

                acc[key] = union([fieldSchema, undef]);
                return acc;
              },
              {} as Record<string, Schema>,
            ),
          );
        } else if (valueType.kind === "UNION") {
          // return a union of partials
          return union(
            valueType.unionTypes.map((x) => {
              return partial(x);
            }),
          );
        } else if (valueType.kind === "MANUAL") {
          return valueType;
        } else {
          throw new Error("Expected interface or union");
        }
      }
      case "PICK": {
        let valueType = this.resolveSchema(schema.valueType);

        if (valueType.kind === "UNION") {
          // this implies the picked field is on every instance of the union,
          // the typechecker checks this for us, so we just pick the first member

          valueType = this.resolveSchema(valueType.unionTypes[0]);
        } else if (valueType.kind === "MANUAL") {
          return valueType;
        }

        if (valueType.kind !== "INTERFACE") {
          throw new Error(`Expected interface type, got ${valueType.kind}`);
        }

        // if the field is a literal, convert it to a union of literals
        const fieldType = schema.fields.kind === "LITERAL" ? union([schema.fields]) : schema.fields;

        return interf(
          fieldType.unionTypes.reduce(
            (acc, x) => {
              if (x.kind === "LITERAL" && typeof x.value === "string") {
                const fieldSchema = valueType.fields[x.value];
                assertIsNotUndefined(fieldSchema);

                acc[x.value] = fieldSchema;
                return acc;
              }

              assert(false, "Expected literal string");
              return acc;
            },
            {} as Record<string, Schema>,
          ),
        );
      }
      case "OMIT": {
        let valueType = this.resolveSchema(schema.valueType);

        if (valueType.kind === "UNION") {
          // this implies the picked field is on every instance of the union,
          // the typechecker checks this for us, so we just pick the first member

          valueType = this.resolveSchema(valueType.unionTypes[0]);
        } else if (valueType.kind === "MANUAL") {
          return valueType;
        }

        if (valueType.kind !== "INTERFACE") {
          throw new Error(`Expected interface type, got ${valueType.kind}`);
        }

        // if the field is a literal, convert it to a union of literals
        const fieldType = schema.fields.kind === "LITERAL" ? union([schema.fields]) : schema.fields;

        return interf(
          Object.keys(valueType.fields).reduce(
            (acc, key) => {
              if (!fieldType.unionTypes.some((x) => x.kind === "LITERAL" && x.value === key)) {
                acc[key] = valueType.fields[key];
              }
              return acc;
            },
            {} as Record<string, Schema>,
          ),
        );
      }
      default:
        return schema;
    }
  }
}

function indent(s: string): string {
  return s
    .split("\n")
    .map((x) => `  ${x}`)
    .join("\n");
}
